import type { LayoutVariables, PaginationViewport } from './../types/index';
import config from 'react-global-configuration';
import type { Langcodes } from '@uncinc/react-drupal-core/types';

import layoutVariables from '../_default/layoutVariables';
import layoutVariablesKNCV from '../_kncv_tuberculose/layoutVariables';

import type { AppOptions } from '../types';

export const IS_SERVER = !(
  typeof window !== 'undefined'
  && window.document
  && window.document.createElement
);
export const IS_CLIENT = !IS_SERVER;

export let APP_ENV = process.env.REACT_APP_ENV;
export const __DEBUG__ = APP_ENV !== 'production';
export const USE_SENTRY = process.env.REACT_APP_SENTRY_ENABLED?.toLowerCase() === "true";
export const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
export const REACT_APP_VERSION = process.env.REACT_APP_VERSION;

export let MULTILINGUAL = false;
export let DEFAULT_LANGUAGE: Langcodes = (process.env.REACT_APP_DEFAULT_LANGUAGE ?? (IS_CLIENT ? document.documentElement.getAttribute('lang') : null) ?? 'en') as Langcodes;
export const SITE_NAME = '<SITE NAME>';

export let API_URL = process.env.REACT_APP_API_URL || "/api";
// export let API_URL = 'http://localhost:3000/api'; // <- For local proxy.

// Searchkit
export const SEARCH_PATH = String(DEFAULT_LANGUAGE === 'nl' ? '/zoeken' : '/search');
export let SEARCHKIT_HOST_URL = process.env.REACT_APP_SEARCHKIT_HOST_URL || '/api-proxy/elasticsearch-proxy?_api_proxy_uri=';
export let SEARCHKIT_INDEX_ALL_CONTENT = process.env.REACT_APP_SEARCHKIT_HOST_URL || true;

export const PAGINATION_VIEWPORT: PaginationViewport = {
  xs: 1,
  sm: 3,
  md: 3,
  lg: 5,
  xl: 5,
}

export let APP_OPTIONS: AppOptions = {
  apiUrl: API_URL,
  element: '',
  parameters: {
    env: 'production',
    multilingual: false,
    defaultLanguage: DEFAULT_LANGUAGE,
    searchkitIndexAllContent: 'elasticsearch_index_content_all',
    searchkitIndexAllEvents: 'elasticsearch_index_all_events',
    searchkitIndexAllEventsRecurring: 'elasticsearch_index_events_recurring',
    searchkitIndexAllProjects: 'elasticsearch_index_projects_all',
    searchkitIndexAllLocations: 'elasticsearch_index_location_all_entities',
    contactBlockID: 4,
    copyRight: 'Company BV',
    vacancyCompanyName: 'Company BV',
    vacancyCompanyCOC: '12345678',
    vacancyCompanyURL: 'example.org',
    vacancyCompanyLogo: 'imageurl.example.org',
    vacancyEmailAddress: 'example@example.org',
    vacancyEmailSubject: 'Application',
    vacancyEmailBody: 'example@example.org',
    privacyStatementUrl: '/privacy-statement',
    siteName:'Cloud'
  },
};

// Either the theme is set using an env var (e.g. in SSR context), or it is set using the data-theme attribute on the html element.
const currentTheme: string = process.env.REACT_APP_CLIENT ?? (IS_CLIENT ? document.documentElement.getAttribute('data-theme') : null) ?? 'default';

// Load the correct layout variables based on the theme.
export const LAYOUT_VARIABLES: LayoutVariables = (function () {
  switch (currentTheme) {
    case 'kncv_tuberculose':
    case 'kncv_tuberculosis':
      return layoutVariablesKNCV;

    default:
      return layoutVariables;
  }
})();

// If the data-theme attribute is not set, set it to the current theme.
if (IS_CLIENT && !document.documentElement.getAttribute('data-theme')) {
  document.documentElement.setAttribute('data-theme', currentTheme);
}

// The value is where the breakpoint starts (mobile-first).
// These values are taken from '/src/src/styles/assets/_variables.scss'.
export const SCREEN_XS = 0; // xs
export const SCREEN_SM = 576; // sm
export const SCREEN_MD = 768; // md
export const SCREEN_LG = 1200; // lg
export const SCREEN_XL = 1560; // xl

export const MAIN_CONTENT_ID: string = 'main-content';

export const CONTENT_TYPE_ARTICLE = 'article';
export const CONTENT_TYPE_BASIC_PAGE = 'basic_page';
export const CONTENT_TYPE_EVENT = 'event';
export const CONTENT_TYPE_FAQ = 'faq';
export const CONTENT_TYPE_HOMEPAGE = 'homepage';
export const CONTENT_TYPE_LANDING_PAGE = 'landing_page';
export const CONTENT_TYPE_OVERVIEW_PAGE = 'overview_page';
export const CONTENT_TYPE_PARTNER ='partner';
export const CONTENT_TYPE_PROJECT = 'project';
export const CONTENT_TYPE_VACANCY = 'vacancy';

// Cards.
export const CARD_SUBTITLE_MAX_LENGTH = 75;

// Use config to share static configuration between components and packages.
config.set({
  'SITE_NAME': SITE_NAME,
  'MULTILINGUAL': false,
  'DEFAULT_LANGUAGE': DEFAULT_LANGUAGE,
  'API_URL': API_URL,
  'REACT_APP_VERSION': REACT_APP_VERSION,
  'PAGE_TRANSITION_SPEED': 100,
  // Set freeze to false to allow overwriting the configs based on the
  // options passed to window.reactFrontend
}, { freeze: false });

export function saveConstants(options: AppOptions) {
  APP_OPTIONS = options;

  if (options.parameters.env) {
    APP_ENV = options.parameters.env;
  }

  if (options.apiUrl) {
    API_URL = options.apiUrl;
  }

  if (APP_ENV === 'development') {
    // Use proxy in dev to prevent CORS issues.
    API_URL = 'http://localhost:3000/api';
    SEARCHKIT_HOST_URL = 'http://localhost:3000/search-proxy';
  }

  if (options.parameters.multilingual) {
    MULTILINGUAL = options.parameters.multilingual;
  }

  if (options.parameters.defaultLanguage) {
    DEFAULT_LANGUAGE = options.parameters.defaultLanguage;
  }

  // Update the config.
  config.set({
    'SITE_NAME': SITE_NAME,
    'MULTILINGUAL': MULTILINGUAL,
    'DEFAULT_LANGUAGE': DEFAULT_LANGUAGE,
    'API_URL': API_URL,
    'REACT_APP_VERSION': REACT_APP_VERSION,
    'PAGE_TRANSITION_SPEED': 100,
  });
}
